import React, { useState, useEffect } from 'react';
import { Modal, Card, Row, Col, Select, Spin, Button, Typography, Badge, Space, Input, Table, DatePicker } from 'antd';
import { ShoppingOutlined, UserOutlined, NumberOutlined, UserSwitchOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { calculateCFT, getWidthQtyTotal } from '../../../../utils/cftCalculator';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

interface ProductItem {
    pid_id: number;
    pid_product_id: number;
    product_name: string;
    total_qty: number;
}

interface EmployeeItem {
    employee_id: number;
    employee_fname: string;
    employee_lname: string;
    employee_department: string | number;
    department_name?: string;
}

interface DepartmentItem {
    department_id: number;
    department_name: string;
}

interface WorkOrderModalProps {
    visible: boolean;
    onCancel: () => void;
    pi: {
        pi_id: number;
        pi_order_no: string;
    } | null;
    onSuccess?: () => void;
}

const ALLOWED_DEPARTMENTS = ["Machinery", "Assembly", "Sanding", "Finishing", "Packing"];

const WorkOrderModal: React.FC<WorkOrderModalProps> = ({ visible, onCancel, pi, onSuccess }) => {
    interface Assignment {
        id?: number;
        department_id: number | null;
        employee_id: number | null;
        qty: number;
        wo_due_date?: string | null;
    }

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<ProductItem[]>([]);
    const [employees, setEmployees] = useState<EmployeeItem[]>([]);
    const [departments, setDepartments] = useState<DepartmentItem[]>([]);
    const [boms, setBoms] = useState<any[]>([]);
    const [woods, setWoods] = useState<any[]>([]);
    const [woodTypes, setWoodTypes] = useState<any[]>([]);
    const [assignments, setAssignments] = useState<Record<number, Assignment[]>>({});
    const [piDetail, setPiDetail] = useState<any>(null);
    const [hoveredCard, setHoveredCard] = useState<number | null>(null);

    useEffect(() => {
        if (visible && pi) {
            setLoading(true);
            Promise.all([
                apiRequest<any>({
                    method: 'get',
                    url: `/get-track-pi-detail/${pi.pi_id}`
                }),
                apiRequest<EmployeeItem[]>({
                    method: 'get',
                    url: '/employees?status=1'
                }),
                apiRequest<DepartmentItem[]>({
                    method: 'get',
                    url: '/departments?status=1'
                }),
                apiRequest<any[]>({
                    method: 'get',
                    url: '/get-bom'
                }).catch(() => []),
                apiRequest<any[]>({
                    method: 'get',
                    url: '/woods?status=1'
                }).catch(() => []),
                apiRequest<any[]>({
                    method: 'get',
                    url: '/wood-types?status=1'
                }).catch(() => [])
            ]).then(([piRes, empRes, deptRes, bomRes, woodRes, woodTypeRes]) => {
                setPiDetail(piRes);
                setEmployees(empRes || []);
                const filteredDepts = (deptRes || [])
                    .filter((d: any) =>
                        ALLOWED_DEPARTMENTS.some(allowed => allowed.toLowerCase() === d.department_name.toLowerCase())
                    )
                    .sort((a: any, b: any) => {
                        const indexA = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === a.department_name.toLowerCase());
                        const indexB = ALLOWED_DEPARTMENTS.findIndex(allowed => allowed.toLowerCase() === b.department_name.toLowerCase());
                        return indexA - indexB;
                    });
                setDepartments(filteredDepts);
                setBoms(bomRes || []);
                setWoods(woodRes || []);
                setWoodTypes(woodTypeRes || []);
                const loadedItems = (piRes?.items || []).map((item: any) => ({
                    ...item,
                    total_qty: Math.round((Number(item.total_qty) || 0) * 100) / 100
                }));
                setItems(loadedItems);

                const initialAssignments: Record<number, Assignment[]> = {};
                const workOrders = piRes?.workOrders || [];
                loadedItems.forEach((item: any) => {
                    const itemWOs = workOrders.filter((wo: any) => wo.wo_pid_detail_id === item.pid_id);
                    if (itemWOs.length > 0) {
                        const existingAssigns: Assignment[] = itemWOs.map((wo: any) => {
                            const emp = (empRes || []).find((e: any) => e.employee_id === wo.wo_employee_id);
                            return {
                                id: wo.wo_id,
                                department_id: emp && emp.employee_department
                                    ? (typeof emp.employee_department === 'string'
                                        ? parseInt(emp.employee_department.split(',')[0], 10)
                                        : emp.employee_department)
                                    : null,
                                employee_id: wo.wo_employee_id,
                                qty: Number(wo.wo_qty),
                                wo_due_date: wo.wo_due_date || null
                            };
                        });
                        const assignedSum = existingAssigns.reduce((sum, row) => sum + row.qty, 0);
                        const roundedSum = Math.round(assignedSum * 100) / 100;
                        if (roundedSum < item.total_qty) {
                            existingAssigns.push({
                                department_id: null,
                                employee_id: null,
                                qty: Math.round((item.total_qty - roundedSum) * 100) / 100,
                                wo_due_date: null
                            });
                        }
                        initialAssignments[item.pid_id] = existingAssigns;
                    } else {
                        initialAssignments[item.pid_id] = [{ department_id: null, employee_id: null, qty: item.total_qty, wo_due_date: null }];
                    }
                });
                setAssignments(initialAssignments);
            }).catch(err => {
                console.error(err);
                showError('Failed to load work order data.');
            }).finally(() => {
                setLoading(false);
            });
        } else {
            setItems([]);
            setAssignments({});
            setPiDetail(null);
            setDepartments([]);
            setBoms([]);
            setWoods([]);
            setWoodTypes([]);
        }
    }, [visible, pi]);

    const handleAddAssignmentRow = (pidId: number, maxQty: number) => {
        setAssignments(prev => {
            const current = prev[pidId] || [];
            const assignedSum = current.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
            const remaining = Math.max(0, maxQty - assignedSum);
            return {
                ...prev,
                [pidId]: [...current, { department_id: null, employee_id: null, qty: remaining, wo_due_date: null }]
            };
        });
    };

    const handleRemoveAssignmentRow = (pidId: number, index: number) => {
        setAssignments(prev => {
            const current = [...(prev[prev[pidId] ? pidId : 0] || prev[pidId] || [])];
            current.splice(index, 1);
            return { ...prev, [pidId]: current };
        });
    };

    const handleUpdateAssignment = (pidId: number, index: number, field: keyof Assignment, val: any) => {
        setAssignments(prev => {
            const current = [...(prev[pidId] || [])];
            if (field === 'department_id') {
                current[index] = { ...current[index], department_id: val, employee_id: null };
            } else {
                current[index] = { ...current[index], [field]: val };
            }
            return { ...prev, [pidId]: current };
        });
    };

    const getDeptQtySums = (pidId: number) => {
        const sums: Record<string, number> = {};
        const list = assignments[pidId] || [];
        list.forEach(row => {
            const deptKey = row.department_id !== null && row.department_id !== undefined ? String(row.department_id) : 'unspecified';
            sums[deptKey] = (sums[deptKey] || 0) + (Number(row.qty) || 0);
        });
        return sums;
    };

    const hasDeptQtyError = (pidId: number, totalQty: number) => {
        const sums = getDeptQtySums(pidId);
        return Object.values(sums).some(sum => Math.round(sum * 100) / 100 > totalQty);
    };

    const renderDeptSums = (item: ProductItem) => {
        const sums = getDeptQtySums(item.pid_id);
        const elements: React.ReactNode[] = [];

        Object.entries(sums).forEach(([deptIdStr, qty]) => {
            const roundedQty = Math.round(qty * 100) / 100;
            const isOver = roundedQty > item.total_qty;
            let deptName = 'Unspecified';
            if (deptIdStr !== 'unspecified') {
                const deptId = parseInt(deptIdStr, 10);
                const dept = departments.find(d => d.department_id === deptId);
                if (dept) {
                    deptName = dept.department_name;
                }
            }
            elements.push(
                <div key={deptIdStr} style={{ display: 'flex', justifyContent: 'space-between', color: isOver ? '#ef4444' : '#64748b', fontWeight: 600, fontSize: 11, marginTop: 4 }}>
                    <span>{deptName}: {roundedQty} / {item.total_qty} pcs</span>
                    {isOver && <span style={{ color: '#ef4444', fontWeight: 700 }}>Exceeds!</span>}
                </div>
            );
        });

        return elements;
    };

    const getTimberTableData = (productId: number, qty: number) => {
        const bom = boms.find(b => Number(b.bom_product_id) === Number(productId));
        if (!bom || !bom.components || bom.components.length === 0) return { rows: [], activeWidthKeys: [] };

        const rows: any[] = [];
        const widthSet = new Set<string>();

        bom.components.forEach((comp: any, cIdx: number) => {
            (comp.woodRows || []).forEach((wRow: any, wIdx: number) => {
                let rawWidthsObj = wRow.widths || wRow.bwr_widths || {};
                if (typeof rawWidthsObj === 'string') {
                    try {
                        rawWidthsObj = JSON.parse(rawWidthsObj);
                    } catch (e) {
                        rawWidthsObj = {};
                    }
                }
                const actualWidthsObj: { [key: string]: number } = {};
                let mixQtyFromWidth1 = 0;

                Object.entries(rawWidthsObj).forEach(([w, q]) => {
                    if (String(w) === "1") {
                        mixQtyFromWidth1 = Number(q) || 0;
                    } else {
                        actualWidthsObj[w] = Number(q) || 0;
                        if (Number(q) > 0) widthSet.add(String(w));
                    }
                });

                const woodMaster = woods.find(w => Number(w.wood_id) === Number(wRow.bwr_wood_id));
                const woodTypeObj = woodTypes.find(wt => Number(wt.wood_id) === Number(wRow.bwr_wood_type_id));

                const length = woodMaster ? Number(woodMaster.wood_length) : 5.25;
                const thickness = woodMaster ? Number(woodMaster.wood_thickness) : 1.5;
                const typeLabel = woodTypeObj?.wood_name
                    ? woodTypeObj.wood_name.charAt(0).toUpperCase()
                    : woodMaster?.wood_name || "S";

                const sizeWoodType = `${length}' x ${thickness}" (${typeLabel})`;
                const widthQtyTotal = getWidthQtyTotal(rawWidthsObj);
                const unitCft = calculateCFT(widthQtyTotal, length, thickness);
                const totalCft = parseFloat((unitCft * Number(qty)).toFixed(4));
                const price = Number(wRow.bwr_rate) || 0;
                const totalAmount = parseFloat((totalCft * price).toFixed(4));

                const mixQty = mixQtyFromWidth1 || Number(wRow.mix) || Number(wRow.mix_qty) || 0;
                const sumWidthPcs = Object.values(actualWidthsObj).reduce((sum: number, q: any) => sum + (Number(q) || 0), 0);
                const totalPcs = sumWidthPcs > 0 ? sumWidthPcs * Number(qty) : (mixQty || 100) * Number(qty);

                rows.push({
                    key: `${cIdx}-${wIdx}`,
                    size_wood_type: sizeWoodType,
                    mix: mixQty > 0 ? mixQty * Number(qty) : (sumWidthPcs > 0 ? "" : 100 * Number(qty)),
                    widths: Object.fromEntries(
                        Object.entries(actualWidthsObj).map(([w, q]) => [w, Number(q) * Number(qty)])
                    ),
                    cft: totalCft,
                    pcs: totalPcs,
                    price: price,
                    total_amount: totalAmount,
                });
            });
        });

        const activeWidthKeys = Array.from(widthSet).sort((a, b) => Number(a) - Number(b));
        if (activeWidthKeys.length === 0) {
            activeWidthKeys.push("4", "5");
        }

        return { rows, activeWidthKeys };
    };

    const getWoSequenceNo = (targetPidId: number, targetRowIdx: number) => {
        let count = 0;
        for (const item of items) {
            const list = assignments[item.pid_id] || [];
            for (let i = 0; i < list.length; i++) {
                count++;
                if (item.pid_id === targetPidId && i === targetRowIdx) {
                    return count;
                }
            }
        }
        return count + 1;
    };

    const handleSave = async () => {
        if (!pi?.pi_id) return;

        // Perform UI quantity validation check
        for (const item of items) {
            const hasNegative = (assignments[item.pid_id] || []).some(row => (Number(row.qty) || 0) < 0);
            if (hasNegative) {
                showError(`Assigned quantity for ${item.product_name} cannot be negative.`);
                return;
            }
            if (hasDeptQtyError(item.pid_id, item.total_qty)) {
                showError(`Assigned quantity for ${item.product_name} in one or more departments exceeds the total quantity of ${item.total_qty}.`);
                return;
            }
        }

        const payloadAssignments: Record<number, any[]> = {};
        Object.entries(assignments).forEach(([pidIdStr, list]) => {
            const pidId = parseInt(pidIdStr, 10);
            payloadAssignments[pidId] = list.map((row, idx) => {
                const woSeq = getWoSequenceNo(pidId, idx);
                return {
                    ...row,
                    wo_number: `${pi?.pi_order_no}-${woSeq}`
                };
            });
        });

        try {
            setLoading(true);
            await apiRequest({
                method: 'post',
                url: '/save-work-orders',
                data: {
                    pi_id: pi.pi_id,
                    assignments: payloadAssignments
                }
            });
            showSuccess('Work order assignments saved successfully!');
            if (onSuccess) {
                onSuccess();
            }
            onCancel();
        } catch (err: any) {
            console.error(err);
            const serverMsg = err?.response?.data?.message || 'Failed to save work order assignments.';
            showError(serverMsg);
        } finally {
            setLoading(false);
        }
    };

    const assignedCount = items.filter(item => {
        const list = assignments[item.pid_id] || [];
        return list.some(row => row.employee_id && row.qty > 0);
    }).length;

    const hasValidationError = items.some(item => {
        const hasNegative = (assignments[item.pid_id] || []).some(row => (Number(row.qty) || 0) < 0);
        return hasDeptQtyError(item.pid_id, item.total_qty) || hasNegative;
    });

    return (
        <Modal
            title={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ background: '#e0e7ff', padding: '4px 8px', borderRadius: 6, display: 'inline-flex' }}>
                            <UserSwitchOutlined style={{ color: '#4f46e5' }} />
                        </span>
                        Work Order Assignments
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                        Assign products under PI #{pi?.pi_order_no} to specific employees.
                    </Text>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            width="85%"
            centered
            destroyOnHidden
            styles={{
                header: { borderBottom: '1px solid #f0f0f0', paddingBottom: 12 },
                body: { padding: '20px 0' },
                footer: { borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 12 }
            }}
            footer={[
                <div key="footer-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Badge count={assignedCount} showZero color="#10b981" />
                        <Text type="secondary" style={{ fontSize: 13 }}>Products Assigned</Text>
                        <Text type="secondary" style={{ fontSize: 13 }}>|</Text>
                        <Badge count={items.length - assignedCount} showZero color="#94a3b8" />
                        <Text type="secondary" style={{ fontSize: 13 }}>Pending</Text>
                    </div>
                    <Space>
                        <Button key="cancel" onClick={onCancel} style={{ borderRadius: 6 }}>Cancel</Button>
                        <Button key="save" type="primary" onClick={handleSave} disabled={loading || hasValidationError} style={{ borderRadius: 6, background: hasValidationError ? '#94a3b8' : '#4f46e5', borderColor: hasValidationError ? '#94a3b8' : '#4f46e5' }}>
                            Save Assignments
                        </Button>
                    </Space>
                </div>
            ]}
        >
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
                    <Spin size="large" tip="Loading products and employees..." />
                </div>
            ) : items.length === 0 ? (
                <div style={{ padding: '60px 0', textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 15 }}>No products found in this Performa Invoice.</Text>
                </div>
            ) : (
                <div style={{ padding: '0 24px' }}>
                    {/* PI Info Summary Banner */}
                    {piDetail && (
                        <div style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            padding: '16px 20px',
                            borderRadius: 12,
                            marginBottom: 20,
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 16
                        }}>
                            <div>
                                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Client / Company</Text>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>
                                    {piDetail.ledger_company_name || `${piDetail.ledger_first_name || ''} ${piDetail.ledger_last_name || ''}`.trim() || 'N/A'}
                                </div>
                            </div>
                            <div>
                                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>PI Date</Text>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginTop: 2 }}>
                                    {piDetail.pi_date ? dayjs(piDetail.pi_date).format('DD MMM YYYY') : 'N/A'}
                                </div>
                            </div>
                            <div>
                                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Lead Time</Text>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginTop: 2 }}>
                                    {piDetail.pi_lead_time} Days
                                </div>
                            </div>
                            <div>
                                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Items</Text>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#4f46e5', marginTop: 2 }}>
                                    {items.length} Products
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cards Grid */}
                    <Row gutter={[16, 16]}>
                        {items.map((item) => {
                            const currentList = assignments[item.pid_id] || [];
                            const isAssigned = currentList.some(r => r.employee_id && r.qty > 0);
                            const isHovered = hoveredCard === item.pid_id;

                            const sums = getDeptQtySums(item.pid_id);
                            const isOverLimit = hasDeptQtyError(item.pid_id, item.total_qty);

                            const maxDeptSum = Math.max(0, ...Object.values(sums));
                            const isFullyAssigned = isAssigned && Math.round(maxDeptSum * 100) / 100 === item.total_qty;
                            const isPartiallyAssigned = isAssigned && !isFullyAssigned && !isOverLimit;

                            return (
                                <Col xs={24} sm={12} md={12} lg={8} key={item.pid_id}>
                                    <Card
                                        hoverable
                                        onMouseEnter={() => setHoveredCard(item.pid_id)}
                                        onMouseLeave={() => setHoveredCard(null)}
                                        style={{
                                            borderRadius: 12,
                                            border: isHovered
                                                ? '1px solid #818cf8'
                                                : isOverLimit
                                                    ? '1px solid #ef4444'
                                                    : isFullyAssigned
                                                        ? '1px solid #c6f6d5'
                                                        : isPartiallyAssigned
                                                            ? '1px solid #bfdbfe'
                                                            : '1px solid #e2e8f0',
                                            boxShadow: isHovered
                                                ? '0 12px 20px -8px rgba(99, 102, 241, 0.15)'
                                                : '0 4px 12px rgba(0,0,0,0.02)',
                                            overflow: 'hidden',
                                            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                            position: 'relative',
                                            background: isOverLimit
                                                ? '#fef2f2'
                                                : isFullyAssigned
                                                    ? '#f6fdf9'
                                                    : isPartiallyAssigned
                                                        ? '#f0f7ff'
                                                        : '#ffffff'
                                        }}
                                        styles={{
                                            body: { padding: 18 }
                                        }}
                                    >
                                        {/* Accent top line */}
                                        <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 4,
                                            background: isOverLimit
                                                ? 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)'
                                                : isFullyAssigned
                                                    ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                                                    : isPartiallyAssigned
                                                        ? 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)'
                                                        : 'linear-gradient(90deg, #6366f1 0%, #a5b4fc 100%)'
                                        }} />

                                        {/* Status Tag */}
                                        <div style={{ position: 'absolute', top: 12, right: 12 }}>
                                            {isOverLimit ? (
                                                <Badge status="error" text={<Text style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Error</Text>} />
                                            ) : isFullyAssigned ? (
                                                <Badge status="success" text={<Text style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Fully Assigned</Text>} />
                                            ) : isPartiallyAssigned ? (
                                                <Badge status="processing" text={<Text style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>Partially Assigned</Text>} />
                                            ) : (
                                                <Badge status="default" text={<Text style={{ fontSize: 11, color: '#64748b' }}>Unassigned</Text>} />
                                            )}
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
                                            {/* Product Name */}
                                            <div>
                                                <Space style={{ fontSize: 11, color: '#64748b' }} size={4}>
                                                    <ShoppingOutlined />
                                                    <span style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Product Name</span>
                                                </Space>
                                                <div style={{
                                                    fontSize: 15,
                                                    fontWeight: 700,
                                                    color: '#1e293b',
                                                    marginTop: 4,
                                                    lineHeight: 1.3,
                                                    height: 40,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}>
                                                    {item.product_name}
                                                </div>
                                            </div>

                                            {/* Qty & PI No Info Grid */}
                                            <Row gutter={8}>
                                                <Col span={12}>
                                                    <div style={{
                                                        background: isOverLimit ? '#fee2e2' : isAssigned ? '#ecfdf5' : '#f8fafc',
                                                        padding: '8px 12px',
                                                        borderRadius: 8,
                                                        border: isOverLimit ? '1px solid #fca5a5' : isAssigned ? '1px solid #d1fae5' : '1px solid #f1f5f9',
                                                        transition: 'all 0.3s'
                                                    }}>
                                                        <Text type="secondary" style={{ fontSize: 10, color: '#64748b' }}>Total Qty</Text>
                                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>
                                                            {item.total_qty} <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b' }}>pcs</span>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col span={12}>
                                                    <div style={{
                                                        background: isOverLimit ? '#fee2e2' : isAssigned ? '#ecfdf5' : '#f8fafc',
                                                        padding: '8px 12px',
                                                        borderRadius: 8,
                                                        border: isOverLimit ? '1px solid #fca5a5' : isAssigned ? '1px solid #d1fae5' : '1px solid #f1f5f9',
                                                        transition: 'all 0.3s'
                                                    }}>
                                                        <Space style={{ fontSize: 10, color: '#64748b' }} size={2}>
                                                            <NumberOutlined style={{ fontSize: 9 }} />
                                                            <span>PI No</span>
                                                        </Space>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                                                            #{pi?.pi_order_no}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            {/* Employee Assignments List */}
                                            <div>
                                                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <UserOutlined />
                                                    <span style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>WO Number & Employee Assignment</span>
                                                </div>

                                                {currentList.map((row, idx) => {
                                                    const woSeq = getWoSequenceNo(item.pid_id, idx);
                                                    const woNumber = `${pi?.pi_order_no}-${woSeq}`;

                                                    return (
                                                        <div key={idx} style={{ marginBottom: 12, borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
                                                            {/* Row 1: WO Number, Select Dept, Delete Button */}
                                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                                                <div style={{ minWidth: 90, flexShrink: 0 }}>
                                                                    <Text style={{
                                                                        fontSize: 11,
                                                                        fontWeight: 700,
                                                                        color: '#4f46e5',
                                                                        background: '#e0e7ff',
                                                                        padding: '5px 8px',
                                                                        borderRadius: 6,
                                                                        display: 'inline-block',
                                                                        textAlign: 'center',
                                                                        width: '100%'
                                                                    }}>
                                                                        {woNumber}
                                                                    </Text>
                                                                </div>
                                                                <div style={{ flex: 1 }}>
                                                                    <Select
                                                                        showSearch
                                                                        placeholder="Select Dept"
                                                                        style={{ width: '100%' }}
                                                                        optionFilterProp="label"
                                                                        value={row.department_id || undefined}
                                                                        onChange={(val) => handleUpdateAssignment(item.pid_id, idx, 'department_id', val)}
                                                                        options={departments.map(dept => ({
                                                                            label: dept.department_name,
                                                                            value: dept.department_id
                                                                        }))}
                                                                        dropdownStyle={{ borderRadius: 8 }}
                                                                    />
                                                                </div>
                                                                {currentList.length > 1 && (
                                                                    <Button
                                                                        type="text"
                                                                        danger
                                                                        icon={<DeleteOutlined />}
                                                                        onClick={() => handleRemoveAssignmentRow(item.pid_id, idx)}
                                                                        style={{ padding: '4px 8px' }}
                                                                    />
                                                                )}
                                                            </div>

                                                            {/* Row 2: Select Employee, Qty Input */}
                                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, paddingLeft: 98 }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <Select
                                                                        showSearch
                                                                        placeholder="Select Employee"
                                                                        style={{ width: '100%' }}
                                                                        optionFilterProp="label"
                                                                        value={row.employee_id || undefined}
                                                                        onChange={(val) => handleUpdateAssignment(item.pid_id, idx, 'employee_id', val)}
                                                                        disabled={!row.department_id}
                                                                        options={employees
                                                                            .filter(emp => {
                                                                                if (!emp.employee_department) return false;
                                                                                const deptIds = String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
                                                                                return deptIds.includes(Number(row.department_id));
                                                                            })
                                                                            .map(emp => ({
                                                                                label: `${emp.employee_fname || ''} ${emp.employee_lname || ''}`.trim() || `Employee #${emp.employee_id}`,
                                                                                value: emp.employee_id
                                                                            }))}
                                                                        dropdownStyle={{ borderRadius: 8 }}
                                                                    />
                                                                </div>
                                                                <div style={{ flex: '0 0 75px' }}>
                                                                    <Input
                                                                        type="number"
                                                                        placeholder="Qty"
                                                                        min={0}
                                                                        value={row.qty || 0}
                                                                        onChange={(e) => {
                                                                            const val = Math.max(0, Number(e.target.value));
                                                                            handleUpdateAssignment(item.pid_id, idx, 'qty', val);
                                                                        }}
                                                                        style={{ width: '100%', borderRadius: 6 }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingLeft: 98 }}>
                                                                <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>Due Date:</span>
                                                                <DatePicker
                                                                    placeholder="Select Due Date"
                                                                    style={{ flex: 1, borderRadius: 6 }}
                                                                    value={row.wo_due_date ? dayjs(row.wo_due_date) : null}
                                                                    onChange={(date, dateString) => {
                                                                        const dateVal = Array.isArray(dateString) ? dateString[0] : dateString;
                                                                        handleUpdateAssignment(item.pid_id, idx, 'wo_due_date', dateVal || null);
                                                                    }}
                                                                />
                                                            </div>
                                                            {(() => {
                                                                const selectedDept = departments.find(d => d.department_id === row.department_id);
                                                                const isTimber = selectedDept && (
                                                                    selectedDept.department_name.toLowerCase().includes('timber') ||
                                                                    selectedDept.department_name.toLowerCase().includes('machinery')
                                                                );

                                                                if (!isTimber || !row.qty || row.qty <= 0) return null;

                                                                const { rows: timberRows, activeWidthKeys } = getTimberTableData(item.pid_product_id, row.qty);
                                                                if (timberRows.length === 0) return null;

                                                                const columns = [
                                                                    {
                                                                        title: "Wood / Type",
                                                                        dataIndex: "size_wood_type",
                                                                        key: "size_wood_type",
                                                                        width: 120,
                                                                    },
                                                                    {
                                                                        title: "Mix",
                                                                        dataIndex: "mix",
                                                                        key: "mix",
                                                                        width: 60,
                                                                        render: (val: any) => val || "-",
                                                                    },
                                                                    ...(activeWidthKeys.map(w => ({
                                                                        title: `${w}"`,
                                                                        key: `width_${w}`,
                                                                        align: "center" as const,
                                                                        width: 50,
                                                                        render: (_: any, record: any) => {
                                                                            const val = record.widths?.[w];
                                                                            return val !== undefined && val !== null ? val : "-";
                                                                        }
                                                                    }))),
                                                                    {
                                                                        title: "CFT",
                                                                        dataIndex: "cft",
                                                                        key: "cft",
                                                                        width: 70,
                                                                        render: (cft: number) => <strong>{cft}</strong>,
                                                                    },
                                                                    {
                                                                        title: "Pcs",
                                                                        dataIndex: "pcs",
                                                                        key: "pcs",
                                                                        width: 60,
                                                                    }
                                                                ];

                                                                return (
                                                                    <div style={{ marginTop: 8, width: '100%' }}>
                                                                        <div style={{ fontSize: 10, color: '#4f46e5', fontWeight: 600, marginBottom: 4, paddingLeft: 8 }}>
                                                                            🪵 Timber Wood Specifications (CFT for {row.qty} pcs):
                                                                        </div>
                                                                        <div style={{ width: '100%', overflowX: 'auto', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                                                            <Table
                                                                                columns={columns}
                                                                                dataSource={timberRows}
                                                                                pagination={false}
                                                                                size="small"
                                                                                bordered
                                                                                rowKey="key"
                                                                                style={{ fontSize: 10 }}
                                                                                scroll={{ x: 'max-content' }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    );
                                                })}

                                                <Button
                                                    type="dashed"
                                                    onClick={() => handleAddAssignmentRow(item.pid_id, item.total_qty)}
                                                    icon={<PlusOutlined />}
                                                    style={{ width: '100%', marginTop: 8, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >
                                                    Add Assignment
                                                </Button>

                                                <div style={{ marginTop: 12, borderTop: '1px dashed #f0f0f0', paddingTop: 8 }}>
                                                    <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                                        Dept-wise Summary
                                                    </div>
                                                    {renderDeptSums(item)}
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}
        </Modal>
    );
};

export default WorkOrderModal;
